import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { DEV_MODE, getDeployTarget } from '../config/environment';
import { passesSubscriptionSoftGate } from '../utils/subscriptionEntitlements';
import {
  COMING_SOON_FLAG_KEYS,
  isComingSoonFeatureUnlocked,
  readLocalComingSoonUnlock,
} from '../utils/hamburgerMenu';

/** Defaults when the Firestore doc is missing (safe: staging on, prod off). */
export const DEFAULT_FEATURE_FLAGS = {
  tourPlannerVisibleStaging: true,
  tourPlannerVisibleProduction: false,
  communityVisibleStaging: true,
  communityVisibleProduction: false,
  accountVisibleStaging: true,
  accountVisibleProduction: true,
  fundVisibleStaging: true,
  fundVisibleProduction: false,
  eventsVisibleStaging: true,
  eventsVisibleProduction: false,
  gearVisibleStaging: true,
  gearVisibleProduction: false,
  calendarVisibleStaging: true,
  calendarVisibleProduction: false,
  expenseTrackerVisibleStaging: true,
  expenseTrackerVisibleProduction: false,
  /** Wallet page + hamburger Wallet. Hidden in production until Budget and Map are live. */
  walletVisibleStaging: true,
  walletVisibleProduction: false,
  /** Budget revisions from the 2026-10-21 Lauren meeting (tour picker first, merch page, etc.). */
  budgetRevisionsStaging: true,
  budgetRevisionsProduction: false,
  centeredNavStaging: true,
  centeredNavProduction: false,
  gridNavStaging: false,
  gridNavProduction: false,
  stackedCenterNavStaging: false,
  stackedCenterNavProduction: false,
  simpleCenterNavStaging: false,
  simpleCenterNavProduction: false,
  /** Stacked-center icons on the left with logo first + large title. Overrides other nav layouts. */
  leftLogoNavStaging: true,
  leftLogoNavProduction: false,
  updatedColorSchemeStaging: false,
  updatedColorSchemeProduction: false,
  hideNavOnScrollStaging: true,
  hideNavOnScrollProduction: false,
  fixedFooterStaging: true,
  fixedFooterProduction: false,
  userLocationsVisibleStaging: true,
  userLocationsVisibleProduction: true,
  footerResourcesVisibleStaging: false,
  footerResourcesVisibleProduction: false,
  /** Master Tour Calendar + map UX overhaul. On for staging and production. */
  masterTourCalendarUiStaging: true,
  masterTourCalendarUiProduction: true,
  /** Admin-style account dropdown, tour chats in Discussions → Messages, and chat emails. */
  tourCommunityHubStaging: true,
  tourCommunityHubProduction: false,
  /** Canva header About / Contact / Donate under the title. Off by default. */
  canvaHeaderLinksStaging: false,
  canvaHeaderLinksProduction: false,
  /** Yearly Calendar + Map plan ($220/yr) on /plans. Off by default everywhere. */
  yearlyPricingStaging: false,
  yearlyPricingProduction: false,
};

const FEATURE_FLAGS_CACHE_KEY = 'vrc_feature_flags_v1';

function readCachedFeatureFlags() {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(FEATURE_FLAGS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function writeCachedFeatureFlags(data) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FEATURE_FLAGS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private mode errors
  }
}

const FeatureFlagsContext = createContext({
  flags: DEFAULT_FEATURE_FLAGS,
  loading: true,
});

function navFlagOn(merged, stagingKey, productionKey, deployTarget, { stagingDefaultTrue = false } = {}) {
  if (DEV_MODE) {
    return stagingDefaultTrue
      ? merged[stagingKey] !== false
      : merged[stagingKey] === true;
  }
  if (deployTarget === 'production') return merged[productionKey] === true;
  return stagingDefaultTrue
    ? merged[stagingKey] !== false
    : merged[stagingKey] === true;
}

/** Resolve nav layout flags from merged Firestore data (shared by hooks + header shell). */
export function getNavLayoutFromFlags(flags, deployTarget = getDeployTarget()) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const showLeftLogoNav = navFlagOn(merged, 'leftLogoNavStaging', 'leftLogoNavProduction', deployTarget, { stagingDefaultTrue: true });
  const showSimpleCenterNav = !showLeftLogoNav && navFlagOn(merged, 'simpleCenterNavStaging', 'simpleCenterNavProduction', deployTarget);
  const showStackedCenterNav = !showLeftLogoNav && !showSimpleCenterNav && navFlagOn(merged, 'stackedCenterNavStaging', 'stackedCenterNavProduction', deployTarget);
  const showGridNav = !showLeftLogoNav && !showSimpleCenterNav && !showStackedCenterNav && navFlagOn(merged, 'gridNavStaging', 'gridNavProduction', deployTarget);
  const showCenteredNav = !showLeftLogoNav && !showSimpleCenterNav && !showStackedCenterNav && !showGridNav && navFlagOn(
    merged,
    'centeredNavStaging',
    'centeredNavProduction',
    deployTarget,
    { stagingDefaultTrue: true },
  );
  return { showLeftLogoNav, showSimpleCenterNav, showStackedCenterNav, showGridNav, showCenteredNav };
}

/** Nav layout is layout-critical — wait for flags (or cache) before rendering. */
export function useNavLayout() {
  const { flags, loading } = useFeatureFlags();
  if (loading) {
    return { ready: false, showLeftLogoNav: false, showSimpleCenterNav: false, showStackedCenterNav: false, showGridNav: false, showCenteredNav: false };
  }
  return { ready: true, ...getNavLayoutFromFlags(flags) };
}

/**
 * Staging and production are controlled independently.
 * Local / dev host always behaves as enabled so engineers are not blocked.
 */
export function isTourPlannerFeatureEnabledForDeploy(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged.tourPlannerVisibleStaging !== false;
  const production = merged.tourPlannerVisibleProduction === true;
  if (deployTarget === 'development') return true;
  if (deployTarget === 'staging') return staging;
  if (deployTarget === 'production') return production;
  return true;
}

/** Admins and local dev always see Tour Planner; others need Map+Calendar subscription + feature flag. */
export function useTourPlannerNavVisible(user) {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  const isAdmin = user?.userType === 'admin' || user?.isAdmin === true;
  if (isAdmin || DEV_MODE) return true;
  if (loading) return false;
  if (!isTourPlannerFeatureEnabledForDeploy(flags, deployTarget)) return false;
  return passesSubscriptionSoftGate(user, { requireMap: true });
}

export function isCommunityFeatureEnabledForDeploy(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged.communityVisibleStaging === true;
  const production = merged.communityVisibleProduction === true;
  if (deployTarget === 'development') return true;
  if (deployTarget === 'staging') return staging;
  if (deployTarget === 'production') return production;
  return true;
}

/** Admins and local dev always see Community; others follow deploy + flags. */
export function useCommunityNavVisible(user) {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  const isAdmin = user?.userType === 'admin' || user?.isAdmin === true;
  if (isAdmin || DEV_MODE) return true;
  if (loading) return false;
  return isCommunityFeatureEnabledForDeploy(flags, deployTarget);
}

export function isCalendarFeatureEnabledForDeploy(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged.calendarVisibleStaging !== false;
  const production = merged.calendarVisibleProduction === true;
  if (deployTarget === 'development') return true;
  if (deployTarget === 'staging') return staging;
  if (deployTarget === 'production') return production;
  return true;
}

/** Admins and local dev always see Calendar; others need Calendar or Map+Calendar subscription + feature flag. */
export function useCalendarNavVisible(user) {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  const isAdmin = user?.userType === 'admin' || user?.isAdmin === true;
  if (isAdmin || DEV_MODE) return true;
  if (loading) return false;
  if (!isCalendarFeatureEnabledForDeploy(flags, deployTarget)) return false;
  return passesSubscriptionSoftGate(user, { requireCalendar: true });
}

export function isExpenseTrackerFeatureEnabledForDeploy(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged.expenseTrackerVisibleStaging !== false;
  const production = merged.expenseTrackerVisibleProduction === true;
  if (deployTarget === 'development') return true;
  if (deployTarget === 'staging') return staging;
  if (deployTarget === 'production') return production;
  return true;
}

/** Admins and local dev always see Expense Tracker; others follow deploy + flags. */
export function useExpenseTrackerVisible(user) {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  const isAdmin = user?.userType === 'admin' || user?.isAdmin === true;
  if (isAdmin || DEV_MODE) return true;
  if (loading) return false;
  return isExpenseTrackerFeatureEnabledForDeploy(flags, deployTarget);
}

export function resolveBudgetRevisions(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') return merged.budgetRevisionsProduction === true;
  return merged.budgetRevisionsStaging !== false;
}

/** Revised Budget layout. Local follows staging. Admins see what the deploy shows, so they can check before going live. */
export function useBudgetRevisions() {
  const { flags } = useFeatureFlags();
  return resolveBudgetRevisions(flags, getDeployTarget());
}

/** Logo left, icon grid, large title filling the header. Overrides other nav layouts. */
export function useLeftLogoNav() {
  const { ready, showLeftLogoNav } = useNavLayout();
  return ready ? showLeftLogoNav : null;
}

/** Controls centered-logo nav layout. Affects all users (it's a layout, not a feature gate). */
export function useCenteredNav() {
  const { ready, showCenteredNav } = useNavLayout();
  return ready ? showCenteredNav : null;
}

/** Controls the new 2×3 grid icon nav layout. When enabled, overrides centeredNav. */
export function useGridNav() {
  const { ready, showGridNav } = useNavLayout();
  return ready ? showGridNav : null;
}

/** Hamburger + centered title + profile; no icon grid. Overrides other nav layouts. */
export function useSimpleCenterNav() {
  const { ready, showSimpleCenterNav } = useNavLayout();
  return ready ? showSimpleCenterNav : null;
}

/** Title + icon row stacked in the true center; profile/welcome on the right. Overrides grid + centered nav. */
export function useStackedCenterNav() {
  const { ready, showStackedCenterNav } = useNavLayout();
  return ready ? showStackedCenterNav : null;
}

function isFlagEnabledForDeploy(flags, stagingKey, productionKey, deployTarget, { stagingDefault = false, productionDefault = false } = {}) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged[stagingKey] ?? stagingDefault;
  const production = merged[productionKey] ?? productionDefault;
  if (deployTarget === 'development') return true;
  if (deployTarget === 'staging') return staging === true;
  if (deployTarget === 'production') return production === true;
  return staging === true;
}

/**
 * Master Tour Calendar layout (month cards, week itinerary, day-sheet poster)
 * plus the matching map UX (Pins rail, food hubs).
 * Local / staging follow the staging toggle; production follows the production toggle.
 * While flags are still loading, use the same defaults as the deploy target (do not
 * apply the staging default on production).
 */
export function resolveMasterTourCalendarUi(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') {
    return merged.masterTourCalendarUiProduction === true;
  }
  return merged.masterTourCalendarUiStaging !== false;
}

export function useMasterTourCalendarUi() {
  const { flags } = useFeatureFlags();
  return resolveMasterTourCalendarUi(flags, getDeployTarget());
}

export function resolveTourCommunityHub(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') {
    return merged.tourCommunityHubProduction === true;
  }
  return merged.tourCommunityHubStaging !== false;
}

/** Admin dropdown layout + tour-party inbox/email. Local follows staging. */
export function useTourCommunityHub() {
  const { flags } = useFeatureFlags();
  return resolveTourCommunityHub(flags, getDeployTarget());
}

export function resolveCanvaHeaderLinks(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') {
    return merged.canvaHeaderLinksProduction === true;
  }
  return merged.canvaHeaderLinksStaging === true;
}

export function resolveYearlyPricing(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') {
    return merged.yearlyPricingProduction === true;
  }
  return merged.yearlyPricingStaging === true;
}

/** Yearly plan on /plans. Local follows staging. Off unless an admin turns it on. */
export function useYearlyPricing() {
  const { flags, loading } = useFeatureFlags();
  if (loading) return false;
  return resolveYearlyPricing(flags, getDeployTarget());
}

/** Header About / Contact / Donate. Local follows staging. Off unless an admin turns it on. */
export function useCanvaHeaderLinks() {
  const { flags, loading } = useFeatureFlags();
  if (loading) return false;
  return resolveCanvaHeaderLinks(flags, getDeployTarget());
}

/** Canva per-page colors (venue, gear swap, calendar). Off restores original styling. */
export function useUpdatedColorScheme() {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  if (loading) return false;
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  // Local development follows the staging toggle so admins can preview flag behavior.
  if (deployTarget === 'production') {
    return merged.updatedColorSchemeProduction === true;
  }
  return merged.updatedColorSchemeStaging === true;
}

/** Hide fixed header on scroll-down; show on scroll-up or top hover. */
export function useHideNavOnScroll() {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  if (DEV_MODE) return true;
  if (loading) return DEFAULT_FEATURE_FLAGS.hideNavOnScrollStaging === true;
  return isFlagEnabledForDeploy(
    flags,
    'hideNavOnScrollStaging',
    'hideNavOnScrollProduction',
    deployTarget,
    { stagingDefault: true, productionDefault: false }
  );
}

/** Show User Locations pill/toggle in Tour Planner filters. */
export function isUserLocationsVisibleForDeploy(flags, deployTarget) {
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  const staging = merged.userLocationsVisibleStaging !== false;
  const production = merged.userLocationsVisibleProduction !== false;
  if (deployTarget === 'production') return production;
  // Local development follows the staging toggle so admins can preview flag behavior.
  return staging;
}

export function useUserLocationsFilterVisible() {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  if (loading) return DEFAULT_FEATURE_FLAGS.userLocationsVisibleStaging !== false;
  return isUserLocationsVisibleForDeploy(flags, deployTarget);
}

/** Footer resource category icons (accessibility, safety, environment, education). */
export function useFooterResourcesVisible() {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  if (loading) return false;
  const merged = { ...DEFAULT_FEATURE_FLAGS, ...flags };
  if (deployTarget === 'production') {
    return merged.footerResourcesVisibleProduction === true;
  }
  return merged.footerResourcesVisibleStaging === true;
}

/** Pin footer to bottom of viewport. */
export function useFixedFooter() {
  const { flags, loading } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  if (DEV_MODE) return true;
  if (loading) return DEFAULT_FEATURE_FLAGS.fixedFooterStaging === true;
  return isFlagEnabledForDeploy(
    flags,
    'fixedFooterStaging',
    'fixedFooterProduction',
    deployTarget,
    { stagingDefault: true, productionDefault: false }
  );
}

export function FeatureFlagsProvider({ children }) {
  const [raw, setRaw] = useState(() => readCachedFeatureFlags());
  const [loading, setLoading] = useState(() => readCachedFeatureFlags() === null);

  useEffect(() => {
    const ref = doc(db, 'system', 'featureFlags');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setRaw(data);
        writeCachedFeatureFlags(data);
        setLoading(false);
      },
      (err) => {
        console.warn('featureFlags snapshot', err);
        setRaw({});
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const flags = useMemo(
    () => ({ ...DEFAULT_FEATURE_FLAGS, ...(raw || {}) }),
    [raw]
  );

  const value = useMemo(() => ({ flags, loading }), [flags, loading]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useComingSoonFeatureUnlocked(featureId, user) {
  const { flags } = useFeatureFlags();
  return isComingSoonFeatureUnlocked(featureId, {
    user,
    flags,
    deployTarget: getDeployTarget(),
    localUnlock: readLocalComingSoonUnlock(),
  });
}

export function useComingSoonUnlocks(user) {
  const { flags } = useFeatureFlags();
  const deployTarget = getDeployTarget();
  const localUnlock = readLocalComingSoonUnlock();
  return useMemo(() => {
    const next = {};
    Object.keys(COMING_SOON_FLAG_KEYS).forEach((id) => {
      next[id] = isComingSoonFeatureUnlocked(id, {
        user,
        flags,
        deployTarget,
        localUnlock,
      });
    });
    return next;
  }, [user, flags, deployTarget, localUnlock]);
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
